import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { IntroNote } from "@/components/IntroNote";
import { CollapsibleGroup } from "@/components/CollapsibleGroup";
import { useCollapsible } from "@/components/CollapsibleList";
import { SeverityBadge } from "@/components/SeverityBadge";
import { toast } from "sonner";
import { RefreshCw, Radar } from "lucide-react";

const SOURCES = [
  { id: "", label: "All sources" },
  { id: "recorded_future", label: "Recorded Future" },
  { id: "world_check", label: "World-Check" },
  { id: "dataminr", label: "Dataminr" },
  { id: "gdelt", label: "GDELT News" },
  { id: "opensanctions", label: "OpenSanctions" },
  { id: "dnb", label: "Credit & financial-health feed" },
  { id: "bitsight", label: "Cyber-posture feed" },
  { id: "reprisk", label: "ESG &amp; reputation feed" },
  { id: "sayari", label: "Trade-network &amp; ownership feed" },
  { id: "servicenow", label: "ServiceNow" },
  { id: "jira", label: "Jira" },
  { id: "ariba", label: "SAP Ariba" },
];

const SUBJECT_LABEL = { entity: "Entity", geography: "Geography", theme: "Theme", asset: "Asset", supplier: "Supplier", incident: "Incident", control: "Control" };

// Group signals by severity (highest first) for collapsible scanning.
const SEV_ORDER = ["critical", "high", "medium", "low", "info"];
function groupSignalsBySeverity(signals) {
  const map = {};
  signals.forEach((s) => {
    const k = (s.severity || "info").toLowerCase();
    (map[k] ||= []).push(s);
  });
  return SEV_ORDER.filter((k) => map[k]?.length).map((k) => ({
    key: k,
    label: k.charAt(0).toUpperCase() + k.slice(1),
    signals: map[k],
  }));
}

export default function Signals() {
  const { user } = useAuth();
  const [signals, setSignals] = useState([]);
  const [count, setCount] = useState(0);
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(true);
  const [ingesting, setIngesting] = useState(false);
  const [pullingGdelt, setPullingGdelt] = useState(false);

  const isAdmin = user?.role === "admin";

  const fetchSignals = useCallback((src) => {
    const qs = src ? `?source=${src}&limit=200&light=true` : "?limit=200&light=true";
    return api
      .get(`/signals${qs}`)
      .then(({ data }) => {
        setSignals(data.items);
        setCount(data.count);
      })
      .catch(() => toast.error("Failed to load signals"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchSignals(source);
  }, [source, fetchSignals]);

  const handlePullGdelt = async () => {
    setPullingGdelt(true);
    try {
      const { data } = await api.post("/signals/ingest-gdelt?max_records=30&timespan=1d");
      if (data.error) {
        toast.info("GDELT is rate-limited — please try again shortly.");
      } else {
        toast.success(`Ingested ${data.ingested} GDELT article(s), skipped ${data.skipped_duplicates}.`);
      }
      await fetchSignals(source);
    } catch {
      toast.error("GDELT pull failed");
    } finally {
      setPullingGdelt(false);
    }
  };

  const handleIngest = async () => {
    setIngesting(true);
    setLoading(true);
    try {
      const { data } = await api.post("/signals/ingest", { connectors: "all" });
      toast.success(
        `Ingested ${data.ingested} new signal(s), skipped ${data.skipped_duplicates} duplicate(s).`
      );
      await fetchSignals(source);
    } catch (e) {
      toast.error("Ingest failed");
    } finally {
      setIngesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="signals-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="ek-eyebrow">Signal Ingestion</p>
            <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Live Signals</h1>
            <p className="mt-3 text-base text-ek-ink/65">
              Normalised intelligence from connected sources ·{" "}
              <span className="font-semibold text-ek-ink" data-testid="signals-count">
                {count}
              </span>{" "}
              signals
            </p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <button
                onClick={handlePullGdelt}
                disabled={pullingGdelt}
                data-testid="pull-gdelt-button"
                className="ek-btn-outline !py-3 disabled:opacity-60"
              >
                <RefreshCw size={16} className={pullingGdelt ? "animate-spin" : ""} />
                {pullingGdelt ? "Pulling…" : "Pull GDELT"}
              </button>
              <button
                onClick={handleIngest}
                disabled={ingesting}
                data-testid="ingest-button"
                className="ek-btn disabled:opacity-60"
              >
                <RefreshCw size={16} className={ingesting ? "animate-spin" : ""} />
                {ingesting ? "Ingesting…" : "Ingest signals"}
              </button>
            </div>
          )}
        </div>

        <IntroNote icon={Radar} title="What you're looking at" testid="signals-intro" className="mt-6">
          The live stream of external intelligence — geopolitical, cyber, sanctions and news — normalised into a
          common format and filterable by source. Signals are the raw early-warning inputs that get linked to
          risks and feed the pre-mortem; this is the outside world arriving in real time.
        </IntroNote>

        {/* Source filter chips */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="source-filters">
          {SOURCES.map((s) => {
            const active = source === s.id;
            return (
              <button
                key={s.id || "all"}
                onClick={() => {
                  setLoading(true);
                  setSource(s.id);
                }}
                data-testid={`source-chip-${s.id || "all"}`}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-all ${
                  active
                    ? "border-ek-purple bg-ek-purple text-white"
                    : "border-black/10 bg-white text-ek-ink/60 hover:border-ek-purple/40 hover:text-ek-ink"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Signals — grouped by severity (critical & high expanded by default) */}
        {loading ? (
          <div className="mt-6 flex items-center justify-center gap-2 rounded-md border border-black/5 bg-white py-20 text-ek-ink/65">
            <RefreshCw size={18} className="animate-spin" /> Loading signals…
          </div>
        ) : signals.length === 0 ? (
          <div className="mt-6 flex flex-col items-center justify-center gap-3 rounded-md border border-black/5 bg-white py-20 text-ek-ink/65">
            <Radar size={32} className="text-ek-purple/40" />
            <p className="text-sm">No signals yet.</p>
            {isAdmin && <p className="text-xs">Use “Ingest signals” to pull from connectors.</p>}
          </div>
        ) : (
          <div className="mt-6 space-y-2" data-testid="signals-list">
            {groupSignalsBySeverity(signals).map((g) => (
              <CollapsibleGroup
                key={g.key}
                title={`${g.label} severity`}
                count={g.signals.length}
                defaultOpen={g.key === "critical" || g.key === "high"}
                accent={g.key === "critical"}
                testid={`signals-group-${g.key}`}
              >
                <SignalsTable signals={g.signals} groupKey={g.key} />
              </CollapsibleGroup>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function SignalsTable({ signals, groupKey }) {
  const { visible, Toggle } = useCollapsible(signals, 5, `signals-${groupKey}`);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-ek-ink/65">
          <tr>
            <th className="py-2 pr-4 font-semibold">Severity</th>
            <th className="py-2 pr-4 font-semibold">Source</th>
            <th className="py-2 pr-4 font-semibold">Subject</th>
            <th className="py-2 pr-4 font-semibold">Summary</th>
            <th className="py-2 pr-4 font-semibold">Received</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((s) => (
            <tr
              key={s.signal_id}
              data-testid="signal-row"
              data-source={s.source}
              className="border-t border-black/5 transition-colors hover:bg-ek-purple/[0.03]"
            >
              <td className="py-2.5 pr-4"><SeverityBadge severity={s.severity} /></td>
              <td className="py-2.5 pr-4 font-mono text-xs text-ek-ink/60">{s.source}</td>
              <td className="py-2.5 pr-4">
                <span className="font-medium text-ek-ink">{s.subject?.name}</span>
                <span className="ml-2 text-xs text-ek-ink/65">
                  {SUBJECT_LABEL[s.subject?.type] || s.subject?.type}
                </span>
              </td>
              <td className="max-w-md py-2.5 pr-4 text-ek-ink/70">{s.summary}</td>
              <td className="whitespace-nowrap py-2.5 text-xs text-ek-ink/65">
                {new Date(s.received_at).toLocaleString()}
              </td>
            </tr>
          ))}
          <Toggle colSpan={5} />
        </tbody>
      </table>
    </div>
  );
}
